import React, {
  FC,
  useContext,
  useEffect,
  useState,
  useRef,
  ReactNode,
} from 'react'
import Dropdown from 'components/Dropdown'

import { routes } from 'constants/routes'

import ModalContext from 'containers/ModalManager/ModalContext'
import {
  useGetProjectsQuery,
  ProjectFragment,
  WorkspaceMinimalFragment,
  useGetInitialUserQuery,
} from 'gqlTypes'
import { handleFetchMore } from 'components/InfiniteList'
import { WorkspacesDropdown } from 'modules/workspace/containers/WorkspacesDropdown/WorkspacesDropdown'
import {
  MenuWrapper,
  ProjectsHeader,
  WorkspaceSelectorWrapper,
  ProjectsMenuWrapper,
  WorkspaceHeader,
  StyledPill,
} from './ProjectsMenu.styles'

import ErrorMessage from 'components/ErrorMessage'
import Spinner from 'components/Spinner'

import { WarningMessage } from 'modules/projects/components'
import { useShareDeleteWithUi } from 'modules/shares/components/DeleteShareModal'
import { EmptyTrashModal } from 'modules/shares/components/EmptyTrashModal'

import { icons } from './icons'
import ProjectsList from 'modules/projects/components/ProjectsList'
import { DeleteProject } from 'modules/projects/modals'
import { ProjectLink } from 'modules/projects/types'
import { useFlag } from 'constants/flags'
import { CreateInlineEditor } from '../InlineEditor'

import { LinkItem, DroppableLinkItem, ActionDropdown } from '../Item'

import { CreateNewProjectButton } from './CreateNewProjectButton'
import HelpMenu from './HelpMenu'

/**
 * TYPES
 */
export interface ProjectsMenuProps {
  canUserAdministerShares: boolean
  showWorkspaceSettings?: boolean
  workspace: WorkspaceMinimalFragment
  workspaces: WorkspaceMinimalFragment[]
}

interface ProjectsListContainerProps {
  workspaceId: string
  isUserEditor: boolean
  isUserAdmin: boolean
  onBlur: () => void
  showCreateProjectEditor: boolean
}

interface MenuItem {
  item: ReactNode
  hidden?: boolean
  key: string
}

/**
 * CONSTANTS
 */
const entriesPath = ['workspace', 'projects', 'entries']
const afterPath = ['workspace', 'projects', 'meta', 'after']

export const menuItemsText = {
  account: 'Account',
  updates: 'Updates',
  settings: 'People & Settings',
  allDocuments: 'All Documents',
  sharedWithMe: 'Shared with Me',
  libraries: 'Libraries',
  trash: 'Trash',
  drafts: 'My Drafts',
}

/**
 * HELPER COMPONENTS
 */
const ProjectsListContainer: FC<ProjectsListContainerProps> = props => {
  const { showModal } = useContext(ModalContext)

  const {
    workspaceId,
    isUserEditor,
    isUserAdmin,
    showCreateProjectEditor,
    onBlur,
  } = props
  const { data, error, loading, fetchMore } = useGetProjectsQuery({
    variables: { workspaceId },
    shouldInvalidatePrevious: (prev, curr) =>
      prev?.workspaceId !== curr?.workspaceId,
  })

  const handleOnDelete = (id: string, name: string) => {
    showModal(DeleteProject, {
      projectId: id,
      projectName: name,
      workspaceId,
    })
  }

  // This is for setting autofocus just after creating a new folder
  const autofocusLastCreated = useRef(false)

  // TODO: test it whether it refreshes the list once user revisits the page
  useEffect(() => {
    if (!loading) {
      const loadLatestHandler = handleFetchMore(fetchMore, entriesPath, {
        fetchLatest: true,
        preserveAfter: true,
        afterPath,
      })

      loadLatestHandler()
    }
    // we want that this function would be executed only when
    // we mount the component, we don't want to execute again
    // when any of the properties change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const projectNormalizer = (entries: ProjectFragment[]): ProjectLink[] => {
    return entries
      .sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { numeric: true })
      )
      .map(project => ({
        link: routes.WORKSPACE_PROJECT.create({
          projectId: project.shortId,
          workspaceId,
        }),
        project: project,
      }))
  }

  if (loading && !data?.workspace) {
    return <Spinner.Flex mt={5} primary />
  }

  if (error) {
    return (
      <ErrorMessage.Generic title="Something went wrong while loading projects" />
    )
  }

  if (!data) {
    return null
  }

  const {
    entries: projects,
    meta: { after, totalCount },
  } = data.workspace.projects

  const warningMessageText =
    'Only Editors can create new Projects in your workspace.'

  if (totalCount === 0) {
    if (isUserEditor || isUserAdmin) {
      return <CreateNewProjectButton workspaceId={workspaceId} />
    }

    return <WarningMessage>{warningMessageText}</WarningMessage>
  }

  const projectsList = projectNormalizer(projects)

  const handleLoadMore = handleFetchMore(fetchMore, entriesPath, {
    after,
  })

  const onCreateFolderCompleted = () => {
    autofocusLastCreated.current = true
    onBlur()
  }

  return (
    <>
      {(isUserEditor || isUserAdmin) && showCreateProjectEditor && (
        <CreateInlineEditor
          workspaceId={workspaceId}
          toggleEditor={onBlur}
          onCompleted={onCreateFolderCompleted}
        />
      )}
      <ProjectsList
        onLoadMore={handleLoadMore}
        items={projectsList}
        onDelete={handleOnDelete}
        userCanEdit={isUserEditor}
        userCanAdminister={isUserAdmin}
        workspaceId={workspaceId}
        autofocusLastCreated={autofocusLastCreated}
      />
    </>
  )
}

const renderMenuItems = (menuItems: MenuItem[]): ReactNode =>
  menuItems.map(({ key, item, hidden }) =>
    !hidden ? <React.Fragment key={key}>{item}</React.Fragment> : null
  )

/**
 * MAIN COMPONENTS
 */
export const ProjectsMenu: FC<ProjectsMenuProps> = ({
  canUserAdministerShares,
  workspace,
  workspaces,
  showWorkspaceSettings,
}) => {
  const { showModal } = useContext(ModalContext)
  const hasSharedWithMeEnabled = useFlag('workspace-shared-with-me')

  const workspaceId = workspace.identifier
  const isUserEditor = workspace?.userCanEdit ?? false
  const isFinance = workspace.userRole === 'FINANCE'

  const [showCreateProjectEditor, setShowCreateProject] = useState(false)

  const { data: userData } = useGetInitialUserQuery()
  const showUnseenNotificationBadge =
    userData?.me.hasUnseenNotifications ?? false

  const [deleteShare] = useShareDeleteWithUi()
  const { data } = useGetProjectsQuery({
    variables: { workspaceId },
    shouldInvalidatePrevious: (prev, curr) =>
      prev?.workspaceId !== curr?.workspaceId,
    skip: !workspace,
  })

  const draftsProject = data?.workspace.draftsProject.entries[0]
  const hasDraftsProject = Boolean(draftsProject)

  const totalCount = data?.workspace?.projects.meta.totalCount
  const showProjectsList =
    !isFinance &&
    workspace.status !== 'INACTIVE' &&
    (workspace.userRole !== 'GUEST' || (totalCount || 0) > 0)

  const handleOnAdd = () => {
    setShowCreateProject(true)
  }

  const handleOnBlur = () => {
    setShowCreateProject(false)
  }

  const handleOnEmptyTrash = () => {
    showModal(EmptyTrashModal, {
      workspaceId,
    })
  }

  const getAddButtonHandler = (totalCount: number | undefined) => {
    /**
     * We will hide the add button when we don't have any projects (we show a button) or we
     * are displaying the inline editor
     */
    if (
      !totalCount ||
      totalCount === 0 ||
      showCreateProjectEditor ||
      !(workspace.userCanEdit || canUserAdministerShares)
    ) {
      return
    }

    return handleOnAdd
  }

  // Menu items
  const menuItems: MenuItem[] = [
    {
      item: (
        <LinkItem
          icon={icons.PersonIcon}
          text={menuItemsText.account}
          to={routes.PERSONAL_SETTINGS.create({})}
        />
      ),
      key: 'personal-settings',
    },
    {
      item: (
        <LinkItem
          icon={icons.BellIcon}
          text={menuItemsText.updates}
          additional={
            showUnseenNotificationBadge ? (
              <StyledPill variant={{ kind: 'secondary' }}>New</StyledPill>
            ) : undefined
          }
          to={routes.UPDATES.create({})}
        />
      ),
      hidden: isFinance,
      key: 'updates',
    },
  ]

  const workspaceMenuItems: MenuItem[] = [
    {
      item: (
        <LinkItem
          icon={icons.AllDocuments}
          text={menuItemsText.allDocuments}
          to={routes.WORKSPACE_SHARES.create({ workspaceId })}
        />
      ),
      hidden: isFinance,
      key: 'all-documents',
    },
    {
      item: (
        <LinkItem
          icon={icons.SharedWithMe}
          text={menuItemsText.sharedWithMe}
          to={routes.WORKSPACE_SHARED_WITH_ME.create({ workspaceId })}
        />
      ),
      hidden: isFinance || !hasSharedWithMeEnabled,
      key: 'shared-with-me',
    },
    {
      item: (
        <LinkItem
          icon={icons.LinkLibrary}
          text={menuItemsText.libraries}
          to={routes.WORKSPACE_LIBRARIES.create({ workspaceId })}
        />
      ),
      hidden: isFinance,
      key: 'libraries',
    },
    {
      item: (
        <LinkItem
          /*
        Designers could not come up with one icon that looks perfect on both
        1x and 2x aspect ratio screens at the same time, so they created two.
        */
          icon={icons.MyDraftsProject}
          text={menuItemsText.drafts}
          to={routes.WORKSPACE_DRAFTS.create({ workspaceId })}
        />
      ),
      hidden: isFinance || !hasDraftsProject,
      key: 'drafts',
    },
    {
      item: (
        <LinkItem
          icon={icons.GearIcon}
          text={menuItemsText.settings}
          to={routes.WORKSPACE_SETTINGS.create({ workspaceId })}
        />
      ),
      hidden: !showWorkspaceSettings,
      key: 'workspace-settings',
    },
    {
      item: (
        <DroppableLinkItem
          icon={icons.Trash}
          text={menuItemsText.trash}
          to={routes.WORKSPACE_TRASH.create({ workspaceId })}
          action={
            workspace.userRole === 'ADMIN' ? (
              <ActionDropdown
                text="Trash Options"
                content={
                  <>
                    <Dropdown.Header>Trash</Dropdown.Header>
                    <Dropdown.Item
                      intent="negative"
                      onClick={e => {
                        e.preventDefault()
                        handleOnEmptyTrash()
                      }}>
                      Empty&hellip;
                    </Dropdown.Item>
                  </>
                }
              />
            ) : null
          }
          onDropped={({ shareId }) => deleteShare({ identifier: shareId })}
        />
      ),
      hidden: isFinance || !canUserAdministerShares,
      key: 'trash',
    },
  ]

  return (
    <ProjectsMenuWrapper>
      {workspace && (
        <WorkspaceSelectorWrapper>
          <WorkspacesDropdown workspace={workspace} workspaces={workspaces} />
        </WorkspaceSelectorWrapper>
      )}
      <MenuWrapper>
        {renderMenuItems(menuItems)}

        <WorkspaceHeader>Workspace</WorkspaceHeader>
        {renderMenuItems(workspaceMenuItems)}

        {showProjectsList && (
          <>
            <ProjectsHeader onAdd={getAddButtonHandler(totalCount)} />
            <ProjectsListContainer
              workspaceId={workspaceId}
              isUserEditor={isUserEditor}
              isUserAdmin={canUserAdministerShares}
              onBlur={handleOnBlur}
              showCreateProjectEditor={showCreateProjectEditor}
            />
          </>
        )}
      </MenuWrapper>
      <HelpMenu />
    </ProjectsMenuWrapper>
  )
}
